import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import UserService from './service';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';

const RegisterUserComponent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [otpOption, setOtpOption] = useState('');
  const [infTwilio, setInfTwilio] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [imageLogo, setImageLogo] = useState('');
  const [nameSubmerchant, setNameSubmerchant] = useState('');
  const [tokenStatus, setTokenStatus] = useState<'valid' | 'used' | 'expired' | 'missing'>('valid'); // Estado del token

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm({
    mode: 'onTouched',
  });

  const password = watch('password');
  const passwordConfirmation = watch('passwordConfirmation');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    const fetchData = async () => {
      if (!token || !email) {
        setTokenStatus('missing'); // No se proporcionó token o email
        return;
      }

      try {
        const response = await UserService.getTokenUsed(email, token);
        console.log('Submerchant Data:', response.data);

        const data = response.data as { code_used: boolean };
        if (data.code_used) {
          setTokenStatus('used'); // Token ya fue usado
        }
      } catch (error) {
        console.error('Error fetching submerchant data:', error);
        setTokenStatus('expired'); // Maneja como expirado en caso de error
      }
    };

    fetchData();
  }, [searchParams]);
  useEffect(() => {
    const id = searchParams.get('id');

    const fetchData = async () => {
      try {
        const { data: user } = (await UserService.getIdUser(id)) as {
          data: {
            first_name: string;
            last_name: string;
            email: string;
            mobile_phone: string;
            date_of_birth: string;
          };
        }; // Asegúrate de que `getIdUser` devuelva `user.data` correctamente
        console.log('User Data:', user);

        // Mapeo de los campos del formulario
        setValue('first_name', user.first_name || '');
        setValue('last_name', user.last_name || '');
        setValue('email', user.email || '');
        setValue('mobile_phone', user.mobile_phone || '');
        setValue('date_of_birth', user.date_of_birth ? user.date_of_birth.split('T')[0] : ''); // Ajuste para formato de fecha

        // Obtener datos adicionales del submerchant
        const result = await UserService.getInfomationSubdomain('subdominio1');
        const resultImg = await UserService.getImageSubmerchant('subdominio1');
        const submerchantName =
          (result.data as { response: { name: string }[] }).response[0]?.name ||
          'Unknown Submerchant';
        const submerchantLogo = (resultImg.data as { Logo: string }).Logo || '';

        // Actualizar estados de submerchant
        setNameSubmerchant(submerchantName);
        setImageLogo(submerchantLogo);
      } catch (error) {
        console.error('Error fetching user or submerchant data:', error);
      }
    };

    fetchData();
  }, [searchParams, setValue]);

  const handleOtpOptionClick = (option: string) => {
    setOtpOption(option);
    setValue('otpOption', option);
    setInfTwilio(option === 'phone');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = async (data: any) => {
    setIsLoading(true);

    try {
      const submitData = {
        email: data.email,
        password: data.password,
        subDomain: 'subdominio1',
        date: data.date_of_birth,
        name: data.first_name,
        lastname: data.last_name,
        otpOption: otpOption,
        phone: data.mobile_phone,
        Subdomain: 'subdominio1',
        id_submerchant: searchParams.get('id'),
      };

      await UserService.updateUserInvitation(submitData);

      // Pequeña pausa para mostrar la animación
      await new Promise((resolve) => setTimeout(resolve, 1000));

      navigate('/backoffice');
    } catch (error) {
      console.error('Error during registration:', error);
      setIsLoading(false);
      // Aquí podrías agregar un manejo de errores más específico
    }
  };

  if (tokenStatus === 'missing') {
    return <div className='text-center text-red-500'>Missing token or email.</div>;
  }

  if (tokenStatus === 'used') {
    return (
      <div className='flex flex-col justify-center items-center min-h-screen bg-surface text-white p-4'>
        <div className='shadow-lg shadow-lg p-8 border border-border-strong rounded-2xl w-full max-w-lg bg-background text-center'>
          <div className='flex flex-col items-center mb-6'>
            <div className='flex items-center justify-center p-2 bg-slate-100 rounded-full mb-4'>
              <img
                className='w-20 h-20 rounded-full object-cover'
                src={imageLogo}
                alt='Submerchant Logo'
              />
            </div>
            <h1 className='text-2xl font-semibold'>{nameSubmerchant}</h1>
            <h4 className='text-sm text-foreground-muted'>Create an account</h4>
          </div>
          <p className='text-lg text-foreground mb-6'>
            The token you are trying to use is no longer valid. Please request a new invitation or
            log in if you already have an account.
          </p>
          <button
            className='w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-all'
            onClick={() => (window.location.href = '/backoffice')}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (tokenStatus === 'expired') {
    return (
      <div className='text-center text-red-500'>
        <p>This token has expired.</p>
        <Link to='/request-new-token' className='text-blue-500 underline'>
          Request a new token
        </Link>
      </div>
    );
  }

  return (
    <div className='flex justify-center items-center min-h-screen bg-surface text-white p-4'>
      <div className='shadow-lg shadow-lg p-8 border border-border-strong rounded-2xl w-full max-w-lg bg-background'>
        <div className='flex gap-6 items-center mb-6'>
          <div className='flex items-center justify-center p-2 bg-slate-100 rounded-full'>
            <img
              className='w-20 h-20 rounded-full object-cover'
              src={imageLogo}
              alt='Submerchant Logo'
            />
          </div>
          <div>
            <h1 className='text-2xl font-semibold'>{nameSubmerchant}</h1>
            <h4 className='text-sm text-foreground-muted'>Create an account</h4>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <div className='grid grid-cols-2 md:grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium' htmlFor='first_name'>
                First Name:
              </label>
              <input
                className='text-white w-full p-2 border border-border-strong bg-surface rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none'
                type='text'
                id='first_name'
                {...register('first_name', { required: 'First name is required' })}
              />
              {errors.first_name && (
                <p className='text-red-500 text-sm'>{errors.first_name?.message as string}</p>
              )}
            </div>
            <div>
              <label className='block text-sm font-medium' htmlFor='last_name'>
                Last Name:
              </label>
              <input
                className='text-white w-full p-2 border border-border-strong bg-surface rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none'
                type='text'
                id='last_name'
                {...register('last_name', { required: 'Last name is required' })}
              />
              {errors.last_name && (
                <p className='text-red-500 text-sm'>{errors.last_name?.message as string}</p>
              )}
            </div>
          </div>

          <div className='grid grid-cols-2 md:grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium' htmlFor='mobile_phone'>
                Phone:
              </label>
              <input
                className='text-white w-full p-2 border border-border-strong bg-surface rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none'
                type='tel'
                id='mobile_phone'
                {...register('mobile_phone', { required: 'Phone is required' })}
              />
              {errors.mobile_phone && (
                <p className='text-red-500 text-sm'>{errors.mobile_phone?.message as string}</p>
              )}
            </div>
            <div>
              <label className='block text-sm font-medium' htmlFor='date_of_birth'>
                Date of Birth:
              </label>
              <input
                className='text-white w-full p-2 border border-border-strong bg-surface rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none'
                type='date'
                id='date_of_birth'
                {...register('date_of_birth', { required: 'Date of birth is required' })}
              />
              {errors.date_of_birth && (
                <p className='text-red-500 text-sm'>{errors.date_of_birth?.message as string}</p>
              )}
            </div>
          </div>

          <div>
            <label className='block text-sm font-medium' htmlFor='email'>
              Email:
            </label>
            <input
              className='text-white w-full p-2 border border-border-strong bg-surface rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none'
              type='email'
              id='email'
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^\S+@\S+$/i, message: 'Enter a valid email address' },
              })}
            />
            {errors.email && (
              <p className='text-red-500 text-sm'>{errors.email?.message as string}</p>
            )}
          </div>

          <div className='grid grid-cols-2 md:grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium' htmlFor='password'>
                Password:
              </label>
              <input
                className='text-white w-full p-2 border border-border-strong bg-surface rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none'
                type={showPassword ? 'text' : 'password'}
                id='password'
                {...register('password', { required: 'Password is required' })}
              />
              {errors.password && (
                <p className='text-red-500 text-sm'>{errors.password?.message as string}</p>
              )}
            </div>
            <div>
              <label className='block text-sm font-medium' htmlFor='passwordConfirmation'>
                Confirm Password:
              </label>
              <input
                className='text-white w-full p-2 border border-border-strong bg-surface rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none'
                type={showPassword ? 'text' : 'password'}
                id='passwordConfirmation'
                {...register('passwordConfirmation', {
                  required: 'Password confirmation is required',
                  validate: (value) => value === password || 'Passwords do not match',
                })}
              />
              {errors.passwordConfirmation && (
                <p className='text-red-500 text-sm'>
                  {errors.passwordConfirmation?.message as string}
                </p>
              )}
            </div>
          </div>

          <div className='space-y-2'>
            <label className='text-sm font-medium'>Receive OTP via:</label>
            <div className='flex gap-4 justify-center'>
              {['email', 'phone'].map((option) => (
                <div
                  key={option}
                  className={`cursor-pointer p-3 rounded-lg border ${
                    otpOption === option ? 'border-blue-500 bg-blue-300' : 'border-border-strong'
                  }`}
                  onClick={() => handleOtpOptionClick(option)}
                >
                  <p className='text-sm font-medium'>{option === 'email' ? 'Email' : 'SMS'}</p>
                </div>
              ))}
            </div>
          </div>

          {infTwilio && (
            <div className='bg-blue-600 text-blue-50 text-sm p-3 rounded-lg'>
              By submitting this form, you agree to receive security alerts and 2FA messages. Reply
              STOP to cancel.
            </div>
          )}

          <button
            type='submit'
            className='w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed relative'
            disabled={
              !password || !passwordConfirmation || Object.keys(errors).length > 0 || isLoading
            }
          >
            {isLoading ? (
              <>
                <span className='absolute inset-0 flex items-center justify-center'>
                  <svg
                    className='animate-spin h-5 w-5 text-white'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  <span className='ml-2'>Processing...</span>
                </span>
                <span className='opacity-0'>Register</span>
              </>
            ) : (
              'Register'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RegisterUserComponent;
